"use client";

import * as styles from "./Rating.module.scss";

export interface RatingProps {
	/** Star value out of 5. */
	value: number;
	size?: "large" | "small";
}

const MAX_STARS = 5;
const STAR_PX: Record<NonNullable<RatingProps["size"]>, number> = {
	large: 28,
	small: 20
};

/**
 * Solid star silhouette (the DS `star` icon is hollow, so we render the filled
 * shape directly to match the Figma testimonials). Single closed path on a
 * 24×24 grid; colour comes from the inherited `fill`.
 */
const STAR_PATH =
	"M12 1C12.3807 1 12.7281 1.21631 12.8965 1.55762L15.753 7.3457L22.1446 8.28027C22.5211 8.33531 22.8338 8.59991 22.9512 8.96191C23.0685 9.32384 22.9699 9.72087 22.6973 9.98633L18.0743 14.4883L19.1661 20.8506L19.1797 20.9912C19.1892 21.3183 19.0372 21.6333 18.7676 21.8291C18.4981 22.0248 18.1518 22.0719 17.8438 21.9619L17.7149 21.9053L12 18.8994L6.2852 21.9053L6.1563 21.9619C5.84825 22.0719 5.50197 22.0248 5.23247 21.8291C4.92449 21.6054 4.76968 21.2258 4.83403 20.8506L5.92485 14.4883L1.30278 9.98633C1.03024 9.72087 0.931646 9.32384 1.04888 8.96191C1.16625 8.59991 1.47896 8.33531 1.85552 8.28027L8.24614 7.3457L11.1036 1.55762L11.1749 1.43555C11.3595 1.16556 11.667 1 12 1Z";

/**
 * Star rating derived from a numeric value out of 5. Renders filled brand-teal
 * stars for the earned value and muted stars for the remainder, matching the
 * testimonial Figma. Defaults to the 28px "large" stars the testimonials use.
 * The numeric value is exposed to assistive tech via `aria-label`; the stars
 * themselves are decorative.
 */
export function Rating({ value, size = "large" }: RatingProps) {
	const filled = Math.round(value);
	const px = STAR_PX[size];

	return (
		<div
			className={styles["rating"]}
			data-component="Rating"
			role="img"
			aria-label={`${value.toFixed(1)} out of 5 stars`}
		>
			{Array.from({ length: MAX_STARS }, (_, i) => (
				<svg
					key={i}
					width={px}
					height={px}
					viewBox="0 0 24 24"
					aria-hidden="true"
					focusable="false"
					className={
						i < filled
							? styles["rating__star"]
							: `${styles["rating__star"]} ${styles["rating__star--empty"]}`
					}
				>
					<path d={STAR_PATH} />
				</svg>
			))}
		</div>
	);
}
